import _ from 'lodash';
import { computed, Injectable, Signal, signal, TemplateRef } from '@angular/core';

import { ActivatedRouteService } from 'src/app/infrastructure/services/route-service/activated-route.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { ClaimPage } from 'src/app/infrastructure/models/claim-page';
import { claimName, TClaimName } from 'src/assets/common/enums/claim-name.g';
import { LogService } from 'src/app/infrastructure/services/log.service';
import { SectionButtonCancel } from '../components/button-blocks/models/section-button-cancel';
import { SectionButtonDelete } from '../components/button-blocks/models/section-button-delete';
import { SectionButtonEdit } from '../components/button-blocks/models/section-button-edit';
import { SectionButtonSave } from '../components/button-blocks/models/section-button-save';
import { ApplicationUserService, structureLockedTooltip } from 'src/app/infrastructure/services/application-user.services/application-user.service';
import { _BaseSectionButtonData, ISectionButtonData, sectionButtonData, TNormalizedSectionButtonData } from '../components/button-blocks/models/_base-section-button';
import { pageMode, pageMode as pageMode_value, TPageMode } from 'src/app/infrastructure/models/page-mode';
import { requestName, TRequestName } from 'src/assets/common/enums/request-names.g';
import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function';

@Injectable()
export abstract class _CentralPageService
{
    protected readonly _activatedRouteService = injectRequired(ActivatedRouteService);
    protected readonly _applicationUserService = injectRequired(ApplicationUserService);

    abstract readonly btnCancelData$$: Signal<TNormalizedSectionButtonData>;
    abstract readonly btnDeleteData$$: Signal<TNormalizedSectionButtonData>;
    abstract readonly btnEditData$$: Signal<TNormalizedSectionButtonData>;
    abstract readonly btnSaveData$$: Signal<TNormalizedSectionButtonData>;
    abstract readonly hasEditSection$$: Signal<boolean>;
    abstract readonly onCancelEmit$: Observable<void>;
    abstract readonly onDeleteEmit$: Observable<void>;
    abstract readonly onEditEmit$: Observable<void>;
    abstract readonly onSaveEmit$: Observable<TRequestName | null>;
    abstract readonly pageLayout$$: Signal<TPageMode>;
    abstract readonly pageMode$$: Signal<TPageMode | null>;
    abstract readonly pageMode_initial$$: Signal<TPageMode | null>;
    abstract readonly pageMode_prev$$: Signal<TPageMode | null>;
    abstract readonly pageName_sectionDetails$$: Signal<ClaimPage | null>;
    abstract readonly pageName_sectionEdit$$: Signal<ClaimPage | null>;
    abstract readonly sectionDetails_buttons_template$$: Signal<TemplateRef<any> | null>;
    abstract readonly sectionDetails_extraButtons_template$$: Signal<TemplateRef<any> | null>;
    abstract readonly sectionDetails_show$$: Signal<boolean>;
    abstract readonly sectionEdit_buttons_template$$: Signal<TemplateRef<any> | null>;
    abstract readonly sectionEdit_extraButtons_template$$: Signal<TemplateRef<any> | null>;
    abstract readonly tabVisible$$: Signal<boolean>;
    abstract readonly template_sectionDetails$$: Signal<TemplateRef<any> | null>;
    abstract readonly template_sectionEdit$$: Signal<TemplateRef<any> | null>;

    // Concreet i.p.v. abstract (default true): lagen zonder eigen tab-concept (bv. single-page)
    // hoeven dit niet te implementeren. TabListService overridet dit naar de echte
    // tabList$$-aanwezigheid (hasList$$) — nodig omdat _section-form-component.ts's Escape-handler
    // anders een lege tab-page (geen geautoriseerde tabs) alsnog naar tabPage-modus zou schakelen.
    readonly hasTabs$$: Signal<boolean> = computed(() => true);

    // Edit-sectie klaar om te tonen (model + eigen startrequests, zie _SectionEditComponent._load_track). Bij de wissel
    // Details → Edit gaat dit op false: section-details houdt Details dan zichtbaar en bouwt Edit onzichtbaar op, zodat
    // Edit in één keer compleet verschijnt i.p.v. in twee slagen. Concreet (default true), zoals hasTabs$$.
    private readonly __sectionEditReady$$ = signal(true);
    readonly sectionEditReady$$ = this.__sectionEditReady$$.asReadonly();
    // Vangnet: een Edit die nooit klaar meldt (fout in een request) mag de wissel niet blijven tegenhouden.
    private readonly __sectionEditReady_maxWaitMs = 3000;

    // Rauwe knop-data (bron, geschreven door de setters) is privé; de protected computed eronder
    // is de geëxposeerde, volledig genormaliseerde (zie __buttonData_normalize) variant waar de
    // page-services naar verwijzen.
    private __btnCancelData$$ = signal<ISectionButtonData>(SectionButtonCancel.create({}) as ISectionButtonData);
    // Zelfde claim-poort als Save (BtnPageCancel_Show op de edit-sectie-pagina). Zonder claim-pagina
    // (geen CLAIM_PAGE op de edit-sectie) blijft cancel zichtbaar (SectionButtonCancel.hide = false).
    protected readonly _btnCancelData$$ = computed(() => {
        const __value = this.__btnCancelData$$();
        const __pageName = this.pageName_sectionEdit$$();
        const __result = !__pageName ? __value : this.__sectionButton_setAuth(__value, __pageName, claimName.btnPageCancel_Show);
        return this.__buttonData_normalize(__result);
    });

    private __btnDeleteData$$ = signal<ISectionButtonData>(SectionButtonDelete.create({}) as ISectionButtonData);
    protected readonly _btnDeleteData$$ = computed(() => {
        const __value = this.__btnDeleteData$$();
        const __pageName = this.pageName_sectionDetails$$();
        const __result = !__pageName ? __value : this.__sectionButton_setAuth(__value, __pageName, claimName.btnPageDelete_Show);
        return this.__buttonData_normalize(__result);
    });

    private __btnEditData$$ = signal<ISectionButtonData>(SectionButtonEdit.create({}) as ISectionButtonData);
    protected readonly _btnEditData$$ = computed(() => {
        const __value = this.__btnEditData$$();
        const __pageName = this.pageName_sectionDetails$$();
        const __result = !__pageName ? __value : this.__sectionButton_setAuth(__value, __pageName, claimName.btnPageEdit_Show);
        return this.__buttonData_normalize(__result);
    });

    private __btnSaveData$$ = signal<ISectionButtonData>(SectionButtonSave.create({}) as ISectionButtonData);
    protected readonly _btnSaveData$$ = computed(() => {
        const __value = this.__btnSaveData$$();
        const __pageName = this.pageName_sectionEdit$$();
        const __result = !__pageName ? __value : this.__sectionButton_setAuth(__value, __pageName, claimName.btnPageSave_Show);
        return this.__buttonData_normalize(__result);
    });

    protected readonly _hasEditSection$$ = computed(() => this.template_sectionEdit$$() != null);

    protected _onCancelEmit$ = new BehaviorSubject<void>(undefined);
    protected _onDeleteEmit$ = new BehaviorSubject<void>(undefined);
    protected _onEditEmit$ = new BehaviorSubject<void>(undefined);
    protected _onSaveEmit$ = new BehaviorSubject<TRequestName | null>(null);

    protected _pageLayout$$ = signal<TPageMode>(pageMode.detailsPage, {equal: _.isEqual});
    protected _pageMode$$ = signal<TPageMode | null>(null);
    // Resolved pageMode waarmee de pagina initieel geladen is (zie _BaseRouteComponent._ngOnInit) —
    // het doel voor position_reset()/tabPositionOffStandard$$, niet per se gelijk aan pageMode$$.
    protected _pageMode_initial$$ = signal<TPageMode | null>(null);
    protected _pageMode_prev$$ = signal<TPageMode | null>(null);
    protected _pageName_sectionDetails$$ = signal<ClaimPage | null>(null);
    protected _pageName_sectionEdit$$ = signal<ClaimPage | null>(null);

    protected _sectionDetails_buttons_template$$ = signal<TemplateRef<any> | null>(null);
    protected _sectionDetails_extraButtons_template$$ = signal<TemplateRef<any> | null>(null);
    protected readonly _sectionDetails_show$$ = computed(() => this.pageMode$$() != 'list-page');

    protected _sectionEdit_buttons_template$$ = signal<TemplateRef<any> | null>(null);
    protected _sectionEdit_extraButtons_template$$ = signal<TemplateRef<any> | null>(null);

    protected readonly _tabVisible$$ = computed(() => {
        const _pageMode = this.pageMode$$();
        return _pageMode == 'tab-page' || _pageMode == 'list-page' || _pageMode == 'medium-page';
    });

    protected _template_sectionDetails$$ = signal<TemplateRef<any> | null>(null);
    protected _template_sectionEdit$$ = signal<TemplateRef<any> | null>(null);

    btnCancelData_set(value: ISectionButtonData | null) {
        this.__btnCancelData$$.update(btnData => ({...btnData, ...value}));
    }

    btnDeleteData_set(value: ISectionButtonData | null) {
        this.__btnDeleteData$$.update(btnData => ({...btnData, ...value}));
    }

    btnEditData_set(value: ISectionButtonData | null) {
        this.__btnEditData$$.update(btnData => ({...btnData, ...value}));
    }

    btnSaveData_set(value: ISectionButtonData | null): void {
        this.__btnSaveData$$.update(btnData => ({...btnData, ...value}));
    }

    onCancel_emit(): void {
        this._onCancelEmit$.next();
    }

    onDelete_emit(): void {
        this._onDeleteEmit$.next();
    }

    onEdit_emit(): void {
        this._onEditEmit$.next();
    }

    onSave_emit(): void {
        const __pageMode = this._pageMode$$();
        const __requestNames =
            __pageMode == pageMode.editPage
            ? requestName.edit
            : requestName.create;

        this._onSaveEmit$.next(__requestNames);
    }

    pageLayout_set(pageMode: TPageMode): void  {
        const _pageLayout = this._pageLayout$$();

        if (!!pageMode && pageMode != _pageLayout) {
            this._pageLayout$$.set(pageMode);

            LogService.envDev.logString('pageLayout', `${pageMode}`);
        }
    }

    pageMode_set(pageMode: TPageMode): void  {
        const __pageMode = this._pageMode$$() ?? null;

        if (pageMode && pageMode != __pageMode) {
            this._pageMode$$.set(pageMode);
            this._pageMode_prev$$.set(__pageMode);

            const __fromDetails = __pageMode == pageMode_value.detailsPage || __pageMode == pageMode_value.mediumPage;
            if (pageMode == pageMode_value.editPage && __fromDetails) {
                this.__sectionEditReady$$.set(false);
                setTimeout(() => this.__sectionEditReady$$.set(true), this.__sectionEditReady_maxWaitMs);
            } else {
                this.__sectionEditReady$$.set(true);
            }

            LogService.envDev.logString('pageMode', `${pageMode}`);
        }
    }

    sectionEditReady_set(ready: boolean): void {
        this.__sectionEditReady$$.set(ready);
    }

    pageMode_initial_set(pageMode: TPageMode): void {
        this._pageMode_initial$$.set(pageMode);
    }

    pageName_sectionDetails_set(pageName: ClaimPage) {
        this._pageName_sectionDetails$$.set(pageName);
    }

    pageName_sectionEdit_set(pageName: ClaimPage) {
        this._pageName_sectionEdit$$.set(pageName);
    }

    // SSOT voor alle btnXxxData$$-defaults: ISectionButtonData staat overal optioneel/nullable toe,
    // maar de geëxposeerde signalen garanderen hier één keer een volledig ingevuld object, zodat
    // consumenten nooit zelf ?? hoeven toe te voegen. hide valt terug op true (verbergen) omdat
    // SectionButtonEdit/Delete/Save hem initieel op null zetten totdat __sectionButton_setAuth een
    // echte boolean heeft berekend — de overige velden vallen terug op _BaseSectionButtonData's
    // eigen defaults, puur als vangnet (in de praktijk altijd al ingevuld door de constructor).
    private __buttonData_normalize(data: ISectionButtonData): TNormalizedSectionButtonData {
        return {
            [sectionButtonData.authOverride]: data[sectionButtonData.authOverride] ?? false,
            [sectionButtonData.disabled]: data[sectionButtonData.disabled] ?? true,
            [sectionButtonData.hide]: data[sectionButtonData.hide] ?? true,
            [sectionButtonData.lockOnLoad]: data[sectionButtonData.lockOnLoad] ?? false,
            [sectionButtonData.tooltip]: data[sectionButtonData.tooltip] ?? '',
            [sectionButtonData.tooltipPosition]: data[sectionButtonData.tooltipPosition] ?? 'above',
        };
    }

    private __sectionButton_setAuth(btnData: ISectionButtonData, pageName: ClaimPage, claimName: TClaimName): ISectionButtonData {
        // Sectie-eigen autorisatie (authOverride, bv. owner-override in notice-details):
        // de sectie heeft hide zelf al berekend (incl. claims/owner/lock) — de generieke
        // claim-poort moet dan niet nogmaals verbergen.
        if (btnData[sectionButtonData.authOverride]) { return {...btnData}; }

        const __value = btnData;
        const __pageName = pageName;
        const __btnHide = __value[sectionButtonData.hide] ?? false;
        const __routeKeys = this._activatedRouteService?.routeKeys;
        const __buttonShow =
            !__btnHide && !!__routeKeys && !!__pageName
            ? this._applicationUserService.claim_check(pageName, __routeKeys, claimName)
            : false;

        // Recht aanwezig maar op deze omgeving vergrendeld (structuurpagina's buiten develop): tonen als
        // disabled i.p.v. verbergen, anders lijkt het alsof het recht ontbreekt.
        if (!__buttonShow && !__btnHide && this._applicationUserService.claim_locked(pageName, claimName)) {
            return {...__value, hide: false, disabled: true, tooltip: structureLockedTooltip};
        }

        return {...__value, hide: !__buttonShow};
    }

    sectionDetails_buttons_template_set(templateRef: TemplateRef<any> | undefined) {
        this._sectionDetails_buttons_template$$.set(templateRef ?? null);
    }

    sectionDetails_extraButtons_template_set(templateRef: TemplateRef<any> | undefined) {
        this._sectionDetails_extraButtons_template$$.set(templateRef ?? null);
    }

    sectionDetails_template_set(templateRef: TemplateRef<any> | undefined) {
        if (templateRef) this._template_sectionDetails$$.set(templateRef);
    }

    sectionEdit_buttons_template_set(templateRef: TemplateRef<any> | undefined) {
        this._sectionEdit_buttons_template$$.set(templateRef ?? null);
    }

    sectionEdit_extraButtons_template_set(templateRef: TemplateRef<any> | undefined) {
        this._sectionEdit_extraButtons_template$$.set(templateRef ?? null);
    }

    sectionEdit_template_set(templateRef: TemplateRef<any> | undefined) {
        this._template_sectionEdit$$.set(templateRef ?? null);
    }
}
